/**
 * @engine/core — scripting contracts
 *
 * The language-agnostic boundary for "programmable code": the unified lifecycle
 * ({@link IScriptExecutor}), the capability-scoped engine API ({@link IEngineContext}),
 * and the TypeScript authoring base ({@link Behaviour}). Implementations of the
 * executors and the concrete contexts live in the player/editor (see W4 phases).
 */

export type {
  ApiVersion,
  CollisionInfo,
  ComponentToken,
  EntityHandle,
  EventCaps,
  IEngineBridge,
  IEngineContext,
  IEngineSystemContext,
  InputActionEvent,
  InputCaps,
  LogCaps,
  PhysicsCaps,
  RawColumnAccess,
  RawKeyInputCaps,
  RaycastHit,
  SpriteCaps,
  TimeCaps,
  TransformCaps,
  TransformSnapshot,
  Unsubscribe,
  Vec2Like,
  WorldCaps,
} from './IEngineContext';
export { NO_ENTITY } from './IEngineContext';

export type {
  IScriptExecutor,
  IScriptExecutorFactory,
  ScriptDefinition,
  ScriptFieldDef,
  ScriptLifecycle,
  ScriptMethodDef,
  ScriptType,
} from './IScriptExecutor';

export { Behaviour } from './Behaviour';
export { EngineComponent, isEngineComponent } from './EngineComponent';
export { CameraRigBehaviour } from './CameraRigBehaviour';

export { ComponentStore } from './ComponentStore';
export type {
  ColumnDType,
  ColumnDesc,
  ComponentStoreConfig,
  ComponentStoreSnapshot,
} from './ComponentStore';

export { BackendRegistry } from './BackendRegistry';
export { TypeScriptExecutor } from './TypeScriptExecutor';
export type { BehaviourCtor } from './TypeScriptExecutor';
export { HeadlessWorld, HeadlessEngineContext, HEADLESS_API_VERSION } from './HeadlessEngineContext';
export { ScriptHost } from './ScriptHost';
export type { EngineContextFactory, ScriptAttachment } from './ScriptHost';
export { loadBehaviour } from './loadBehaviour';
export { GameObjectEngineContext, GAMEOBJECT_API_VERSION } from './GameObjectEngineContext';
export type { GameObjectContextDeps } from './GameObjectEngineContext';

// --- Engine-API (the `engine.*` global-services surface; declarative capability modules) ---
export { scriptApi, scriptFn, scriptEvent, getScriptApiMeta, SCRIPT_API_META } from './scriptApi';
export type { ScriptApiTier, ScriptApiOptions, ScriptFnOptions, ScriptApiMeta } from './scriptApi';
export { ScriptApiRegistry, buildEngineApi } from './ScriptApiRegistry';
export type { EngineApiBackings } from './ScriptApiRegistry';
export { makePhysicsBacking } from './makePhysicsBacking';
export type { GuidPhysicsApi, GuidRaycastHit } from './makePhysicsBacking';
export { makeVFXBacking } from './makeVFXBacking';
export type { GuidVFXApi } from './makeVFXBacking';
export { makeCameraBacking } from './makeCameraBacking';
export type { EngineApi, SafeEngineApi, EngineApiNamespace, IPhysicsApi, IEventsApi, ISceneApi, IVfxApi, ICameraApi } from './EngineApi.generated';
export { EngineApiSlots, ENGINE_API_NAMESPACES, ENGINE_API_MODULES, ENGINE_API_VERSION, ENGINE_API_ABI_HASH } from './EngineApi.generated';
export { PhysicsScriptApi } from './modules/PhysicsScriptApi';
export type { PhysicsBacking } from './modules/PhysicsScriptApi';
export { EventsScriptApi } from './modules/EventsScriptApi';
export type { EventsBacking } from './modules/EventsScriptApi';
export { SceneScriptApi } from './modules/SceneScriptApi';
export type { SceneBacking } from './modules/SceneScriptApi';
export { InputScriptApi } from './modules/InputScriptApi';
export type { InputBacking } from './modules/InputScriptApi';
export { AudioScriptApi } from './modules/AudioScriptApi';
export type { AudioBacking } from './modules/AudioScriptApi';
export { VFXScriptApi } from './modules/VFXScriptApi';
export type { VFXBacking } from './modules/VFXScriptApi';
export { CameraScriptApi } from './modules/CameraScriptApi';
export type { CameraBacking } from './modules/CameraScriptApi';
export { UiScriptApi } from './modules/UiScriptApi';
export type { UIBacking, UIScreenHandle } from './modules/UiScriptApi';
export { GpuScriptApi, GpuScope, ComputeKernel, GpuBuffer, GpuTexture } from './modules/GpuScriptApi';
export type {
  GpuBacking,
  GpuKernelHandle,
  GpuKernelResourceInfo,
  GpuElementType,
  GpuTextureFormat,
} from './modules/GpuScriptApi';
